import { KeyboardArrowDown as KeyboardArrowDownIcon } from '@mui/icons-material';
import { createTheme } from '@mui/material/styles';
import type {} from '@mui/x-data-grid/themeAugmentation';

const palette = {
  error: {
    light: '#FFF2F2',
    main: '#DD6262',
  },
  //TODO: Remove this
  gray: {
    light: '#E0E0E0',
    main: '#CCCCCC',
  },
  primary: {
    main: '#3B8EC0',
  },
  secondary: {
    main: '#A3A3A3',
  },
  text: {
    primary: '#2f2f2f',
  },
};

const customPalette = {
  alert: '#D9686C',
  black: '#333333',
  blue: '#3279A3',
  blue2: '#EBF4F9',
  gray: '#CCCCCC',
  gray1: '#B0B7C3',
  gray10: '#B7BCC6',
  gray2: '#828A9B',
  gray3: '#F7F7F7',
  gray5: '#E0E0E0',
  gray6: '#BFCCD8',
  gray7: '#A3A3A3',
  gray8: '#7A7A7A',
  gray9: '#6A6A6A',
  green: '#159E4D',
  lightGray: '#E0E0E0',
  navy: '#0A2252',
  success: '#E8F5ED',
  white: '#fff',
  yellow: '#F4D116',
};

declare module '@mui/material/styles' {
  interface Theme {
    customPalette: {
      alert: string;
      black: string;
      blue: string;
      blue2: string;
      gray: string;
      gray1: string;
      gray10: string;
      gray2: string;
      gray3: string;
      gray5: string;
      gray7: string;
      gray8: string;
      gray9: string;
      green: string;
      lightGray: string;
      navy: string;
      success: string;
      white: string;
      yellow: string;
    };
  }
  interface ThemeOptions {
    customPalette?: {
      alert: string;
      black: string;
      blue: string;
      blue2: string;
      gray: string;
      gray1: string;
      gray10: string;
      gray2: string;
      gray3: string;
      gray5: string;
      gray7: string;
      gray8: string;
      gray9: string;
      green: string;
      lightGray: string;
      navy: string;
      success: string;
      white?: string;
      yellow: string;
    };
  }
  interface PaletteOptions {
    gray: PaletteOptions['primary'];
  }
  interface TypographyVariants {
    body3: React.CSSProperties;
  }
  interface TypographyVariantsOptions {
    body3?: React.CSSProperties;
  }
}

declare module '@mui/material/Typography' {
  interface TypographyPropsVariantOverrides {
    body3: true;
  }
  interface TypographyPropsColorOverrides {
    gray: true;
  }
}

export const theme = createTheme({
  components: {
    MuiAlert: {
      styleOverrides: {
        action: {
          padding: '0 0 0 16px',
        },
        message: {
          padding: '10px 0',
        },
        root: {
          fontSize: '0.75rem',
          padding: '2px 12px',
        },
        standardError: {
          backgroundColor: palette.error.light,
          color: palette.error.main,
        },
        standardInfo: {
          backgroundColor: customPalette.gray3,
        },
        standardSuccess: {
          backgroundColor: customPalette.success,
        },
      },
    },
    MuiAppBar: {
      styleOverrides: {
        root: {
          '&.bottom-area': {
            borderTop: `1px solid ${customPalette.gray5}`,
            bottom: 0,
            top: 'auto',
          },
          '&.header-area': {
            borderBottom: `1px solid ${customPalette.gray5}`,
          },
          backgroundColor: 'white',
          boxShadow: 'none',
        },
      },
    },
    MuiAvatar: {
      styleOverrides: {
        root: {
          backgroundColor: customPalette.gray5,
          color: '#8890A0',
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        containedPrimary: {
          background: 'linear-gradient(45deg, #00E0D5 20%, #3B8EC0 90%)',
          color: 'white',
        },
        outlined: {
          backgroundColor: 'white',
        },
        outlinedSecondary: {
          borderColor: palette.secondary.main,
          color: customPalette.black,
        },
        root: {
          '&.Mui-disabled': {
            background: customPalette.lightGray,
            color: 'white',
          },
          boxShadow: 'none !important',
          fontWeight: 700,
        },
        sizeMedium: {
          fontSize: '1rem',
          padding: '8px 12px',
        },
        sizeSmall: {
          fontSize: '0.75rem',
          padding: '8px',
        },
        textSecondary: {
          color: customPalette.black,
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          boxShadow: 'none',
        },
      },
    },
    MuiCheckbox: {
      styleOverrides: {
        root: {
          '.MuiSvgIcon-root': {
            '&.MuiSvgIcon-fontSizeSmall': {
              fontSize: '1.334rem',
            },
            fontSize: '1.5rem',
          },
          color: customPalette.gray10,
          padding: '4px',
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        colorPrimary: {
          background:
            'linear-gradient(90deg, rgba(0, 224, 213, 0.1) 8.14%, rgba(59, 142, 192, 0.1) 91.86%)',
          color: customPalette.blue,
        },
        root: {
          '.MuiChip-icon': {
            color: 'inherit',
          },
        },
      },
    },
    MuiCssBaseline: {
      styleOverrides: {
        body: {
          MozOsxFontSmoothing: 'grayscale',
          WebkitFontSmoothing: 'antialiased',
          backgroundColor: 'white',
        },
      },
    },
    MuiDataGrid: {
      styleOverrides: {
        cell: {
          ':focus-within': { outline: 'none' },
          fontSize: '0.875rem',
        },
        columnHeader: {
          ':focus-within': { outline: 'none' },
          fontSize: '0.75rem',
        },
        columnHeaders: {
          backgroundColor: customPalette.lightGray,
        },
        root: {
          border: 'none',
        },
        row: {
          '&.Mui-selected': {
            ':hover': {
              backgroundColor: 'rgba(0, 0, 0, 0.04)',
            },
            backgroundColor: 'transparent',
          },
        },
      },
    },
    MuiDivider: {
      styleOverrides: {
        root: {
          borderColor: customPalette.gray5,
        },
      },
    },
    MuiFormHelperText: {
      styleOverrides: {
        root: {
          color: customPalette.gray8,
          fontSize: '0.75rem',
          marginLeft: 0,
          marginRight: 0,
        },
      },
    },
    MuiFormLabel: {
      styleOverrides: {
        root: {
          color: customPalette.black,
          fontSize: '0.75rem',
          marginBottom: '8px',
        },
      },
    },
    MuiIconButton: {
      styleOverrides: {
        colorSecondary: {
          color: customPalette.black,
        },
        root: {
          '&.image-remove': {
            '&:hover': {
              backgroundColor: 'white',
            },
            backgroundColor: 'white',
            color: 'black',
            padding: 0,
            position: 'absolute',
            right: '-0.625rem',
            top: '-0.625rem',
          },
        },
      },
    },
    MuiInputBase: {
      styleOverrides: {
        input: {
          ':not(textarea)': {
            padding: '12px',
          },
        },
        root: {
          '&.Mui-disabled': {
            backgroundColor: 'rgba(0, 0, 0, 0.12)',
          },
          '&.MuiInputBase-multiline': {
            padding: '12px',
          },
          fontSize: '0.875rem',
          lineHeight: 'inherit',
        },
        sizeSmall: {
          height: '36px',
        },
      },
    },
    MuiListItemButton: {
      styleOverrides: {
        root: {
          '&.Mui-selected': {
            ':hover': {
              '.MuiListItemIcon-root': {
                color: 'white',
              },
              backgroundColor: palette.primary.main,
              color: 'white',
            },
            backgroundColor: palette.primary.main,
            color: 'white',
          },
          ':hover': {
            '.MuiListItemIcon-root': {
              color: 'white',
            },
            backgroundColor: palette.primary.main,
            color: 'white',
          },
        },
      },
    },
    MuiMobileStepper: {
      styleOverrides: {
        root: {
          '.MuiIconButton-root': {
            color: 'white',
          },
          backdropFilter: 'blur(4px)',
          backgroundColor: 'rgba(51,51,51,0.4)',
          borderRadius: '16px',
          color: 'white',
          cursor: 'default',
          display: 'inline-flex',
          padding: 0,
        },
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        input: {
          ':not(textarea)': {
            padding: '12px',
          },
          fontSize: '0.875rem',
        },
        notchedOutline: {
          borderColor: customPalette.gray,
        },
        root: {
          '&.border-none > .MuiOutlinedInput-notchedOutline': {
            border: 'none',
          },
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        outlined: {
          border: `1px solid ${customPalette.lightGray}`,
        },
      },
    },
    MuiRadio: {
      styleOverrides: {
        root: {
          '.MuiSvgIcon-root': {
            height: '1rem',
            width: '1rem',
          },
          padding: '8px',
        },
      },
    },
    MuiSelect: {
      defaultProps: {
        IconComponent: KeyboardArrowDownIcon,
      },
    },
    MuiSvgIcon: {
      styleOverrides: {
        root: {
          fontSize: '1.5rem',
        },
      },
    },
    MuiTab: {
      styleOverrides: {
        root: {
          '&.Mui-selected': {
            fontWeight: 700,
          },
          color: customPalette.gray7,
          fontSize: '0.75rem',
          fontWeight: 400,
          minHeight: '32px',
          padding: '8px',
        },
      },
    },
    MuiTablePagination: {
      styleOverrides: {
        actions: {
          '.MuiIconButton-root:not(:disabled)': {
            color: customPalette.gray2,
          },
        },
        displayedRows: {
          fontSize: '1rem',
        },
        select: {
          ':focus': {
            borderRadius: '4px',
          },
          border: `1px solid ${customPalette.gray1}`,
          borderRadius: '4px',
          fontSize: '1rem',
          paddingBottom: '4px',
          paddingTop: '4px',
          textAlignLast: 'left',
          width: '66px',
        },
        selectIcon: {
          color: customPalette.gray2,
          fontSize: '1.5rem',
          marginRight: '8px',
        },
      },
    },
    MuiTabs: {
      styleOverrides: {
        root: {
          minHeight: '32px',
        },
      },
    },
    MuiUseMediaQuery: {
      defaultProps: {
        noSsr: true,
      },
    },
  },
  customPalette,
  palette,
  typography: {
    body1: {
      fontSize: '1rem',
    },
    body2: {
      fontSize: '0.875rem',
    },
    body3: {
      fontSize: '0.75rem',
    },
    fontFamily: [
      '"Segoe UI"',
      '"Meiryo"',
      '-apple-system',
      'BlinkMacSystemFont',
      '"Hiragino Sans"',
      '"Roboto"',
      '"Noto Sans JP"',
      '"Helvetica Neue"',
      'Arial',
      'sans-serif',
      '"Apple Color Emoji"',
      '"Segoe UI Emoji"',
      '"Segoe UI Symbol"',
    ].join(','),
    fontSize: 16,
    h3: {
      fontSize: '2rem',
      fontWeight: 700,
    },
    h4: {
      fontSize: '1.75rem',
      fontWeight: 700,
    },
    h5: {
      fontSize: '1.5rem',
      fontWeight: 700,
    },
    h6: {
      fontSize: '1.375rem',
      fontWeight: 700,
    },
  },
});
